import axios from 'axios';
import { toast } from 'react-hot-toast';
import { extractErrorMessage } from './errorHandler';

// Base API URL - adjust based on environment
const BASE_URL = import.meta.env.VITE_API_URL || 'https://api.card.cartronic.es/api';

// Create axios instance
const api = axios.create({
  baseURL: BASE_URL,
  timeout: 30050, // 30 seconds
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    // Add auth token if available
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // Add request ID for tracking
    config.metadata = {
      startTime: new Date().getTime(),
      requestId: Math.random().toString(36).substr(2, 9),
    };

    // Log request in development
    if (import.meta.env.DEV) {
      console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, {
        headers: config.headers,
        data: config.data,
        params: config.params,
      });
    }

    return config;
  },
  (error) => {
    console.error('Request interceptor error:', error);
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => {
    // Calculate request duration
    const duration = new Date().getTime() - response.config.metadata.startTime;

    // Log response in development
    if (import.meta.env.DEV) {
      console.log(
        `✅ ${response.config.method?.toUpperCase()} ${response.config.url} (${duration}ms)`,
        {
          status: response.status,
          data: response.data,
        }
      );
    }

    // Log slow requests
    if (duration > 2000) {
      console.warn(`🐌 Slow request detected: ${response.config.url} took ${duration}ms`);
    }

    return response;
  },
  (error) => {
    // Calculate request duration if metadata exists
    const duration = error.config?.metadata
      ? new Date().getTime() - error.config.metadata.startTime
      : 0;

    // Log error in development
    if (import.meta.env.DEV) {
      console.error(
        `❌ ${error.config?.method?.toUpperCase()} ${error.config?.url} (${duration}ms)`,
        {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        }
      );
    }

    // Handle different error scenarios
    if (error.response) {
      // Server responded with error status
      const { status, data } = error.response;
      
      switch (status) {
        case 401:
          // Unauthorized - clear token and redirect to login
          if (localStorage.getItem('token')) {
            localStorage.removeItem('token');
            toast.error('Sesión expirada. Por favor, inicia sesión nuevamente.');
            
            // Redirect to login if not already there
            if (window.location.pathname !== '/login') {
              window.location.href = '/login';
            }
          }
          break;

        case 403:
          // Forbidden
          toast.error(data?.message || 'No tienes permisos para realizar esta acción');
          break;

        case 404:
          // Not found - only show toast if it's not a navigation request
          if (!error.config?.url?.includes('/auth/me')) {
            toast.error(data?.message || 'Recurso no encontrado');
          }
          break;

        case 413:
          // Payload too large
          toast.error('El archivo es demasiado grande. Por favor, selecciona un archivo más pequeño.');
          break;

        case 422:
          // Validation error - handle validation errors from backend
          const validationMessage = extractErrorMessage(error, 'Errores de validación');
          toast.error(validationMessage);
          break;

        case 429:
          // Rate limit exceeded
          toast.error(data?.message || 'Demasiadas solicitudes. Por favor, espera un momento.');
          break;

        case 500:
          // Internal server error
          toast.error('Error interno del servidor. Por favor, intenta más tarde.');
          break;

        case 502:
        case 503:
        case 504:
          // Server errors
          toast.error('Servicio temporalmente no disponible. Por favor, intenta más tarde.');
          break;

        default:
          // Other errors - handle both message and errors array from backend
          const errorMessage = extractErrorMessage(error, `Error ${status}: ${error.message}`);
          toast.error(errorMessage);
      }
    } else if (error.request) {
      // Network error
      if (error.code === 'ECONNABORTED') {
        toast.error('La solicitud ha tardado demasiado. Por favor, intenta nuevamente.');
      } else if (error.message === 'Network Error') {
        toast.error('Error de conexión. Verifica tu conexión a internet.');
      } else {
        toast.error('Error de red. Por favor, verifica tu conexión.');
      }
    } else {
      // Other errors
      toast.error('Ha ocurrido un error inesperado.');
    }

    return Promise.reject(error);
  }
);

// API helper functions
export const apiHelpers = {
  // Upload file helper
  uploadFile: (endpoint, file, options = {}) => {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional fields if provided
    if (options.fields) {
      Object.entries(options.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options.onProgress,
      ...options.config,
    });
  },

  // Upload multiple files
  uploadFiles: (endpoint, files, options = {}) => {
    const formData = new FormData();
    
    files.forEach((file, index) => {
      formData.append(`files`, file);
    });
    
    // Add additional fields if provided
    if (options.fields) {
      Object.entries(options.fields).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    return api.post(endpoint, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress: options.onProgress,
      ...options.config,
    });
  },

  // Download file helper
  downloadFile: async (endpoint, filename, options = {}) => {
    try {
      const response = await api.get(endpoint, {
        responseType: 'blob',
        ...options,
      });

      // Create blob URL
      const blob = new Blob([response.data]);
      const url = window.URL.createObjectURL(blob);

      // Create download link
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);

      return response;
    } catch (error) {
      console.error('Download error:', error);
      throw error;
    }
  },

  // Retry helper for failed requests
  retryRequest: (requestConfig, maxRetries = 3, delay = 1000) => {
    return new Promise((resolve, reject) => {
      let retries = 0;

      const makeRequest = () => {
        api(requestConfig)
          .then(resolve)
          .catch((error) => {
            retries++;
            
            if (retries < maxRetries && (!error.response || error.response.status >= 500)) {
              setTimeout(makeRequest, delay * retries);
            } else {
              reject(error);
            }
          });
      };

      makeRequest();
    });
  },

  // Get query string for parameters
  buildQueryString: (params) => {
    const searchParams = new URLSearchParams();
    
    Object.entries(params).forEach(([key, value]) => {
      if (value !== null && value !== undefined && value !== '') {
        if (Array.isArray(value)) {
          value.forEach(v => searchParams.append(key, v));
        } else {
          searchParams.append(key, value);
        }
      }
    });

    return searchParams.toString();
  },
};

// Export specific HTTP methods for convenience
export const get = (url, config) => api.get(url, config);
export const post = (url, data, config) => api.post(url, data, config);
export const put = (url, data, config) => api.put(url, data, config);
export const patch = (url, data, config) => api.patch(url, data, config);
export const del = (url, config) => api.delete(url, config);

export default api; 

// Profiles API helpers
export const profilesApi = {
  list: () => api.get('/profiles').then(r => r.data),
  create: (data) => api.post('/profiles', data).then(r => r.data),
  get: (id) => api.get(`/profiles/${id}`).then(r => r.data),
  update: (id, data) => api.put(`/profiles/${id}`, data).then(r => r.data),
  remove: (id) => api.delete(`/profiles/${id}`).then(r => r.data),
  replicateChanges: (id) => api.post(`/profiles/${id}/replicate`).then(r => r.data),
};